import { trackDuration } from "../PlayerCore/trackContract.js"

/**
 * The portal → player messages applied to the shell. `init` stores each mode's resume position,
 * seeks the active track to its own position first (so a later toggle away and back resumes
 * there), then switches silently (no play, no `mode` event: the portal chose it) when the portal
 * asks for the other mode. `player` returns the PlayerShell instance.
 */
export function createPortalControl({ player, tracks, resume }) {
    const trackFor = (mode) => tracks.find((track) => track.kind === mode) ?? null

    return {
        handle(message) {
            const shell = player()

            if (!shell) {
                return
            }

            switch (message.type) {
                case "init": {
                    for (const [mode, seconds] of Object.entries(message.resume ?? {})) {
                        const track = trackFor(mode)

                        if (track) {
                            resume[track.id] = seconds
                        }
                    }

                    const active = shell.activeTrack()
                    const seconds = active ? (resume[active.id] ?? 0) : 0

                    if (seconds > 0) {
                        shell.seek(seconds, { programmatic: true })
                    }

                    if (trackFor(message.mode) && message.mode !== shell.mode()) {
                        shell.setMode(message.mode, { play: false, report: false })
                    }

                    if (message.autoplay) {
                        shell.play()
                    }

                    return
                }
                case "seek":
                    shell.seek(message.position)

                    return
                case "play":
                    shell.play()

                    return
                case "pause":
                    shell.pause()

                    return
                case "mode":
                    if (trackFor(message.mode)) {
                        shell.setMode(message.mode)
                    }
            }
        },
        readyFields() {
            const active = player()?.activeTrack()

            if (!active) {
                return null
            }

            return {
                duration: trackDuration(active) || null,
                modes: tracks.map((track) => track.kind),
                mode: active.kind,
                items: Object.fromEntries(tracks.map((track) => [track.kind, track.itemId])),
            }
        },
    }
}
